import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import Plot from 'react-plotly.js';
import { DATE_AXIS_TICK_FORMAT_STOPS, toEasternPlotDate } from '../utils/sensorConfig';

/**
 * OxygenSaturationChart Component
 * Displays bottom depth (depth 2) oxygen saturation percentage over time
 * with color-coded background zones indicating different saturation levels
 */
const OxygenSaturationChart = ({ transformedData, calculateOxygenSaturation, xRange }) => {

  const oxygenSaturationData = useMemo(() => {
    if (!transformedData) return [];

    // Prefer o2_25 (µmol/L, needs × 0.032), fall back to o2_rdo (already mg/L)
    const o2_25Bottom = (transformedData.o2_25 || []).filter(r => Number(r.depth) === 2);
    const o2_rdoBottom = (transformedData.o2_rdo || []).filter(r => Number(r.depth) === 2);
    const useRdo = !o2_25Bottom.length && o2_rdoBottom.length > 0;
    const o2Data = useRdo ? o2_rdoBottom : o2_25Bottom;
    if (!o2Data.length) return [];

    // Saturation depends on the water temperature at the BOTTOM (depth 2), where
    // the oxygen is measured. Use the corrected RDO temperature; fall back to the
    // raw RDO bottom temperature if corrected data isn't available.
    const correctedBottom = (transformedData.temperature_rdo_corrected || []).filter(r => Number(r.depth) === 2);
    const tempData = correctedBottom.length
      ? correctedBottom
      : (transformedData.temperature_rdo || []).filter(r => Number(r.depth) === 2);
    if (!tempData.length) return [];

    const tempMap = new Map(tempData.map(r => [r.time, r.sensor_value]));

    const satFromMgL = (o2_mg, tempC) => {
      if (o2_mg == null || tempC == null || tempC >= 289.15) return null;
      const Ts = Math.log((289.15 - tempC) / (273.15 + tempC));
      const O2_sat_100 = 1.42905 * Math.exp(
        2.00907 + 3.22014 * Ts + 4.05010 * Math.pow(Ts, 2) +
        4.94457 * Math.pow(Ts, 3) - 0.256847 * Math.pow(Ts, 4) + 3.88767 * Math.pow(Ts, 5)
      );
      return O2_sat_100 > 0 ? (o2_mg / O2_sat_100) * 100 : null;
    };

    return o2Data
      .map(o2r => {
        const tempC = tempMap.get(o2r.time);
        if (tempC == null) return null;
        const o2_mg = useRdo ? o2r.sensor_value : o2r.sensor_value * 0.032;
        const sat = satFromMgL(o2_mg, tempC);
        if (sat == null || !Number.isFinite(sat)) return null;
        return { x: toEasternPlotDate(o2r.time), y: sat, o2_mg, temp: tempC };
      })
      .filter(Boolean)
      .sort((a, b) => a.x - b.x);
  }, [transformedData]);

  // Generate plot data with proper formatting
  const generateOxygenPlotData = () => {
    if (oxygenSaturationData.length === 0) return [];

    return [{
      x: oxygenSaturationData.map(d => d.x),
      y: oxygenSaturationData.map(d => d.y),
      type: 'scatter',
      mode: 'lines',
      name: 'Bottom Depth O₂ Saturation',
      line: {
        color: '#0277bd',
        width: 3
      },
      marker: {
        color: '#0277bd',
        size: 6
      },
      customdata: oxygenSaturationData.map(d => ({
        saturation: d.y.toFixed(1),
        o2_mg: d.o2_mg.toFixed(2),
        temp: d.temp.toFixed(1)
      })),
      hovertemplate:
        '<b>Bottom Depth</b><br>' +
        '%{x|%Y-%m-%d %H:%M}<br>' +
        'Saturation: %{customdata.saturation}%<br>' +
        'DO: %{customdata.o2_mg} mg/L<br>' +
        'Temp: %{customdata.temp}°C' +
        '<extra></extra>'
    }];
  };

  // Define background shapes for different saturation zones
  const getBackgroundShapes = () => {
    if (oxygenSaturationData.length === 0) return [];

    const xMin = xRange ? xRange[0] : oxygenSaturationData[0].x;
    const xMax = xRange ? xRange[1] : oxygenSaturationData[oxygenSaturationData.length - 1].x;

    return [
      // Severe hypoxia: 0-25% (Red with opacity)
      {
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: xMin,
        y0: -0.5,
        x1: xMax,
        y1: 25,
        fillcolor: 'rgba(211, 47, 47, 0.15)',
        line: { width: 0 },
        layer: 'below'
      },
      // Mild hypoxia: 25-50% (Orange with opacity)
      {
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: xMin,
        y0: 25,
        x1: xMax,
        y1: 50,
        fillcolor: 'rgba(245, 124, 0, 0.12)',
        line: { width: 0 },
        layer: 'below'
      },
      // Under-saturated: 50-80% (Yellow with opacity)
      {
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: xMin,
        y0: 50,
        x1: xMax,
        y1: 80,
        fillcolor: 'rgba(255, 235, 59, 0.15)',
        line: { width: 0 },
        layer: 'below'
      },
      // Saturated: >80% (Green with opacity)
      {
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: xMin,
        y0: 80,
        x1: xMax,
        y1: 150, // Extended beyond 100% to accommodate potential supersaturation
        fillcolor: 'rgba(76, 175, 80, 0.12)',
        line: { width: 0 },
        layer: 'below'
      }
    ];
  };

  // Add annotations for zone labels
  const getZoneAnnotations = () => {
    if (oxygenSaturationData.length === 0) return [];

    const xMid = xRange
      ? new Date((new Date(xRange[0]).getTime() + new Date(xRange[1]).getTime()) / 2)
      : new Date((oxygenSaturationData[0].x.getTime() +
                  oxygenSaturationData[oxygenSaturationData.length - 1].x.getTime()) / 2);

    return [
      {
        x: xMid,
        y: 90,
        text: 'Saturated',
        showarrow: false,
        font: { size: 11, color: '#2e7d32', weight: 600 },
        xanchor: 'center',
        yanchor: 'middle'
      },
      {
        x: xMid,
        y: 65,
        text: 'Low Saturation',
        showarrow: false,
        font: { size: 11, color: '#f57c00', weight: 600 },
        xanchor: 'center',
        yanchor: 'middle'
      },
      {
        x: xMid,
        y: 37.5,
        text: 'Mild Hypoxia',
        showarrow: false,
        font: { size: 11, color: '#e65100', weight: 600 },
        xanchor: 'center',
        yanchor: 'middle'
      },
      {
        x: xMid,
        y: 12.5,
        text: 'Severe Hypoxia',
        showarrow: false,
        font: { size: 11, color: '#c62828', weight: 600 },
        xanchor: 'center',
        yanchor: 'middle'
      }
    ];
  };

  return (
    <Card className="timeseries-chart-card">
      <CardContent>
        <Box className="timeseries-chart-header">
          <Typography variant="h5" component="h2" className="timeseries-chart-title">
            Bottom Depth Oxygen Saturation
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            Bottom Pond Dissolved Oxygen Levels
          </Typography>
        </Box>

        {oxygenSaturationData.length === 0 ? (
          <Box className="no-timeseries-data">
            <Typography variant="body1">
              No oxygen saturation data available for bottom depth. Temperature and dissolved oxygen readings are required.
            </Typography>
          </Box>
        ) : (
          <>
            <Box className="timeseries-chart-container">
              <Plot
                data={generateOxygenPlotData()}
                layout={{
                  height: 400,
                  margin: { l: 80, r: 20, t: 20, b: 120 },
                  xaxis: {
                    title: 'Date & Time',
                    tickangle: -45,
                    type: 'date',
                    tickformatstops: DATE_AXIS_TICK_FORMAT_STOPS,
                    ...(xRange && { range: xRange }),
                  },
                  yaxis: {
                    title: {
                      text: 'Oxygen Saturation (%)',
                      font: {
                        size: 14,
                        color: '#333'
                      },
                      standoff: 15
                    },
                    // Small negative floor so a flat 0% line renders just above
                    // the axis baseline instead of disappearing into it.
                    range: [-0.5, Math.max(120, Math.max(...oxygenSaturationData.map(d => d.y)) * 1.05)],
                    gridcolor: '#e0e0e0'
                  },
                  shapes: getBackgroundShapes(),
                  annotations: getZoneAnnotations(),
                  showlegend: true,
                  legend: {
                    x: 1,
                    xanchor: 'right',
                    y: 1,
                    yanchor: 'top',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    bordercolor: '#ccc',
                    borderwidth: 1
                  },
                  plot_bgcolor: 'white',
                  paper_bgcolor: 'transparent',
                  hovermode: 'x unified'
                }}
                config={{ displayModeBar: false, responsive: true }}
                style={{ width: '100%' }}
              />
            </Box>

            {/* Legend for saturation zones */}
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: 'rgba(76, 175, 80, 0.3)',
                  border: '1px solid rgba(76, 175, 80, 0.5)',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Saturated (&gt;80%)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: 'rgba(255, 235, 59, 0.3)',
                  border: '1px solid rgba(255, 235, 59, 0.5)',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Low Saturdation (80%-50%)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: 'rgba(245, 124, 0, 0.3)',
                  border: '1px solid rgba(245, 124, 0, 0.5)',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Mild Hypoxia (50%-25%)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: 'rgba(211, 47, 47, 0.3)',
                  border: '1px solid rgba(211, 47, 47, 0.5)',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Severe Hypoxia (&lt;25%)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1.6 }}>
              <strong>Note:</strong> Oxygen saturation is calculated from dissolved oxygen (o2_25 or o2_rdo) and water temperature at the pond bottom.
              Low bottom oxygen levels (hypoxia) can lead to sediment phosphorus releases that trigger algal blooms, including cyanobacteria blooms.
            </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OxygenSaturationChart;